// Pieces shared by the monetization pages: revenue basis / attribution toggles (kept in the URL), target status
// badges (icon + label, never colour alone) and the ESTIMATED marker for projections.

import type { RevenueAssumptions, RevenueBasis, RoasAttributionParam, TargetStatus } from '@growth/client';
import { useSearchParams } from 'react-router';
import { fmtPct } from '../format.ts';
import { Icon } from './ui.tsx';

export function useMoneyParams() {
  const [sp, setSp] = useSearchParams();
  const basis: RevenueBasis = sp.get('basis') === 'gross' ? 'gross' : 'net';
  const raw = sp.get('attribution');
  const attribution: RoasAttributionParam = raw === 'shared' || raw === 'blended' ? raw : 'auto';
  const set = (k: string, v: string | null) =>
    setSp(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === null) next.delete(k);
        else next.set(k, v);
        return next;
      },
      { replace: true },
    );
  return {
    basis,
    attribution,
    setBasis: (b: RevenueBasis) => set('basis', b === 'net' ? null : b),
    setAttribution: (a: RoasAttributionParam) => set('attribution', a === 'auto' ? null : a),
  };
}

export function BasisToggle({ value, onChange }: { value: RevenueBasis; onChange: (b: RevenueBasis) => void }) {
  return (
    <div
      className="segmented"
      role="group"
      aria-label="Base de ingresos"
      title="Neto estimado: anuncios (ya netos) + IAP menos la comisión de la tienda (e impuestos si están configurados). Bruto: lo que paga el jugador."
    >
      <button type="button" aria-pressed={value === 'net'} onClick={() => onChange('net')}>
        Neto estimado
      </button>
      <button type="button" aria-pressed={value === 'gross'} onClick={() => onChange('gross')}>
        Bruto
      </button>
    </div>
  );
}

export const ATTRIBUTION_LABEL: Record<RoasAttributionParam, { label: string; help: string }> = {
  auto: { label: 'Auto', help: 'Compartida cuando hay ≥ 30 jugadores atribuidos a la red; si no, mezclada.' },
  shared: { label: 'Compartida', help: 'Jugadores que el SDK atribuye a la red (p. ej. clic de Google Ads) frente a todo su gasto.' },
  blended: { label: 'Mezclada', help: 'Todos los jugadores nuevos, también orgánicos, frente al gasto total.' },
};

export function AttributionToggle({ value, onChange }: { value: RoasAttributionParam; onChange: (a: RoasAttributionParam) => void }) {
  return (
    <div className="segmented" role="group" aria-label="Atribución">
      {(['auto', 'shared', 'blended'] as const).map((a) => (
        <button key={a} type="button" aria-pressed={value === a} title={ATTRIBUTION_LABEL[a].help} onClick={() => onChange(a)}>
          {ATTRIBUTION_LABEL[a].label}
        </button>
      ))}
    </div>
  );
}

export const STATUS: Record<TargetStatus, { label: string; icon: 'good' | 'warning' | 'critical' | 'info'; cls: string; help: string }> = {
  green: { label: 'VERDE', icon: 'good', cls: 'target-green', help: 'En el objetivo o mejor.' },
  yellow: { label: 'AMARILLO', icon: 'warning', cls: 'target-yellow', help: 'Entre el objetivo y el umbral rojo.' },
  red: { label: 'ROJO', icon: 'critical', cls: 'target-red', help: 'Peor que el umbral rojo.' },
  no_target: { label: 'Sin objetivo', icon: 'info', cls: '', help: 'Define objetivos en Configuración ▸ Objetivos. No hay umbrales por defecto.' },
  no_data: { label: 'Sin datos', icon: 'info', cls: '', help: 'Aún no hay datos para esta métrica.' },
  insufficient: { label: 'Muestra pequeña', icon: 'info', cls: '', help: 'Menos de 30 jugadores: el color no sería fiable.' },
};

export function TargetBadge({ status, uncertain }: { status: TargetStatus; uncertain?: boolean }) {
  const s = STATUS[status];
  return (
    <span className={`badge ${s.cls}`} title={`${s.help}${uncertain ? ' El intervalo al 95 % cruza el umbral: puede cambiar con más jugadores.' : ''}`}>
      <Icon name={s.icon} />
      {s.label}
      {uncertain ? ' (?)' : ''}
    </span>
  );
}

export function EstimatedBadge() {
  return (
    <span className="badge estimated" title="Proyección, no ingresos observados. Nunca se mezcla con los valores observados.">
      ESTIMADO
    </span>
  );
}

/** ROAS as a percentage (1 = 100 %). */
export const fmtRoas = (v: number | null | undefined) => fmtPct(v, 0);

export function AssumptionsNote({ a }: { a: RevenueAssumptions | undefined }) {
  if (!a) return null;
  return (
    <p className="small muted" style={{ margin: 0 }}>
      Neto estimado de IAP = bruto {a.taxes_deducted ? `÷ (1 + ${fmtPct(a.iap_tax_rate, 0)} de impuestos) ` : ''}× (1 − {fmtPct(a.iap_store_fee_rate, 0)} de comisión de la
      tienda){a.taxes_deducted ? '' : ': no descuenta los impuestos incluidos en el precio (configúralos en Configuración)'}. Los ingresos por anuncios ya son la parte del
      editor.
    </p>
  );
}
